import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { TYPE } from "../../design/ui";
import {
  typeKey,
  statusKey,
  reviewStatusKey,
  reviewTypeLabel,
  reviewOutcome,
  toPlainText,
  REVIEW_STATUS_LABEL,
} from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPostJson, currentAccount } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import request from "../../utils/request";
import { getToken } from "../../utils/token";
import RichView from "../../components/design/RichView";
import { Banner, Disclosure, StatusChip } from "../../components/mui";
import { PageLoading } from "../../components/loading";
import {
  MdChevronLeft,
  MdChevronRight,
  MdErrorOutline,
  MdInfoOutline,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/* ===================== 小工具 ===================== */

/**
 * 進度回報讀取失敗的記號（與「讀到了、但一筆都沒有」的空陣列分開）。
 *
 * /Task 看得到的人不一定讀得到 /MyTask/Progress（後端依資料範圍擋，擋下來回「任務不存在」）。
 * 吞成空陣列的話，這一頁會少掉「他回報了什麼」整塊、連回報夾的檔案也一起不見 ——
 * 把關者看到的是一張乾淨的畫面，會直接讀成「他從頭到尾沒回報過、沒交付物」然後按下去。
 * 作法與 TaskDetailPanel 的 recordsFailed 一致：兩頁對同一件事不能講成兩種話。
 */
const RECORDS_FAILED = Object.freeze({ failed: true });

/** 任一 API 失敗都不能讓整頁掛掉——取不到就回 fallback（作法與 TaskDetailPanel 一致） */
async function safe(fn, fallback) {
  try {
    const r = await fn();
    return r === undefined || r === null ? fallback : r;
  } catch (e) {
    return fallback;
  }
}

/**
 * 「9 月 12 日」。domain 的 fmtDate 回 2026/9/12，那是舊畫面的格式；
 * 新設計一律把日期寫成中文（設計稿 L1245），兩種格式不混用。
 * 這支與 Approval/Index.js 是同一份寫法 —— 兩頁的日期必須長得一樣。
 */
function fmtDay(v) {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(String(v).replace(/\//g, "-"));
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

/** 從某個時間點到今天等了幾天（忽略時分秒，避免同一天被算成 0.4 天）；取不到回 null */
function daysSince(v) {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(String(v).replace(/\//g, "-"));
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * 等待天數 → 標籤語氣。與清單同一組門檻：三天沒人看就是該有人催了。
 * 顏色不寫死，一律交給 StatusChip 的 kind（深色主題才會跟著換）。
 */
function toneByWait(days) {
  if (days >= 3) return "attention";
  if (days >= 1) return "warn";
  return "progress";
}

/** 把關者的決定：0 還沒看 / 1 通過 / 2 要求修改（沿用後端 ReqReviewDecision 的定義） */
function decisionLabel(v) {
  const n = Number(v) || 0;
  if (n === 1) return "通過";
  if (n === 2) return "要求修改";
  return "尚未審核";
}

/** 位元組 → 1.2 MB；拿不到大小回空字串，不要顯示成「0 B」讓人以為是空檔案 */
function fmtSize(n) {
  const b = Number(n) || 0;
  if (b <= 0) return "";
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

/** 排序用的時間值；取不到回 0 */
function timeOf(v) {
  if (!v) return 0;
  const d = new Date(String(v).replace(/\//g, "-"));
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * 下載端點都有 [Authorize]，直接開 <a href> 不會帶 Bearer token（必定 401），
 * 所以先取 blob、再用暫時的 object URL 存檔（作法同 TaskDetailPanel）。
 */
async function downloadBlob(url, name) {
  const blob = await request.get(url, null, {
    responseType: "blob",
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = name || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

/**
 * 下載失敗的原因。responseType 是 blob 時錯誤回應的 body 也是 Blob，
 * 要先讀成文字才拿得到後端寫的 res_msg（同 TaskDetailPanel 的 downloadErrMsg）。
 */
async function downloadErrMsg(e) {
  const data = _.get(e, "response.data");
  if (data && typeof data.text === "function") {
    try {
      const msg = _.get(JSON.parse(await data.text()), "res_msg");
      if (msg) return String(msg);
    } catch (ignore) {
      // body 不是 JSON → 依狀態碼說明
    }
  }
  if (_.get(e, "response.status") === 404) return "找不到此檔案：可能已被刪除，或您沒有檢視此工項的權限。";
  return apiErrorMessage(e, "無法下載此檔案");
}

/* ===================== 頁面 ===================== */

/**
 * 審核畫面 — 照抄設計稿 el-pm-template_4.html:1240-1285（#screen-approval-view）。
 *
 * 舊的審核是一個 720px 抽屜，裡面整包塞進工項詳情的 21 個區塊（需求、解法、DoD、
 * 相依、留言、附件、CI、測試…），審核者得自己在裡面找出「他到底做了沒有」。
 * 設計稿把它翻成一件事：**當初要求什麼 vs 他做了什麼**，兩張卡並排在最上面，
 * 其餘全部收進一個收合區。這兩欄就是這一頁的全部理由，不要再退回成 21 個區塊。
 *
 * 資料自己取自己渲染（不共用 TaskDetailPanel）：那支元件是「工項詳情」，
 * 專案詳情與個人工項兩處還在用它，為了這一頁改它會動到不相干的畫面。
 */
function ApprovalView() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { toast } = useApp();

  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [reviews, setReviews] = useState([]);
  /** 把關者改派紀錄（/Review/List 的 reassigns）—— 沒有別的畫面講得出改派經過，不能弄丟 */
  const [reassigns, setReassigns] = useState([]);
  /** 驗收條件（/Review/List 的 criteria）—— 只拿來判斷「按通過會不會真的完成」，見 passNote */
  const [criteria, setCriteria] = useState([]);
  const [attachments, setAttachments] = useState([]);
  /** 進度回報（/MyTask/Progress/{taskId}）—— 回報時夾的檔案掛在這裡，不在工項附件裡 */
  const [records, setRecords] = useState([]);
  /** 回報讀不到（見 RECORDS_FAILED）：要跟「讀到了、他真的沒回報過」分得開 */
  const [recordsFailed, setRecordsFailed] = useState(false);
  const [members, setMembers] = useState([]);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [busy, setBusy] = useState(false);

  /* ---------- 載入 ---------- */

  const load = useCallback(async () => {
    if (!taskId) return;
    /*
     * /Task 走的是舊的「PayloadHelper 加密信封」（?Data=<jwt>），/Review/List 走純 query。
     * 信封與扁平欄位一起帶，改造中與改造後的後端都認得（同 TaskDetailPanel 的作法）。
     */
    const env = { Data: jwtEncode({ TaskId: Number(taskId) }), id: taskId, taskId };
    const t = await safe(() => apiGet("/Task", env), null);
    setTask(t);

    const [rv, at, rc] = await Promise.all([
      safe(() => apiGet("/Review/List", { taskId }), {}),
      safe(() => apiGet(`/Task/Attachment/List/${taskId}`), []),
      safe(() => apiGet(`/MyTask/Progress/${taskId}`), RECORDS_FAILED),
    ]);
    setReviews(_.get(rv, "reviews", []) || []);
    setReassigns(_.get(rv, "reassigns", []) || []);
    setCriteria(_.get(rv, "criteria", []) || []);
    setAttachments(Array.isArray(at) ? at : []);
    setRecordsFailed(rc === RECORDS_FAILED);
    setRecords(
      rc === RECORDS_FAILED ? [] : Array.isArray(rc) ? rc : _.get(rc, "data_list", []) || []
    );

    /* 成員名冊只為了把帳號換成人名。沒有 project_id 就不打這一支，帳號原樣顯示 */
    const pid = _.get(t, "project_id");
    if (pid) {
      const mb = await safe(
        () =>
          apiGet("/Project/Member/List", {
            Data: jwtEncode({ ProjectId: Number(pid) }),
            projectId: pid,
          }),
        []
      );
      setMembers(Array.isArray(mb) ? mb : []);
    }
  }, [taskId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await load();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [load]);

  /* ---------- 衍生資料 ---------- */

  const nameOf = useCallback(
    (account) => {
      const m = members.find((x) => x.account === account);
      return _.get(m, "name") || account || "";
    },
    [members]
  );

  /** reviews[0] 是最新一筆（後端由新到舊給），畫面狀態全部看它 */
  const latest = reviews[0] || null;
  const latestKey = latest ? reviewStatusKey(latest.status) : null;
  const pending = latestKey === "pending" ? latest : null;

  const me = currentAccount();
  const assignees = useMemo(() => _.get(latest, "assignees", []) || [], [latest]);

  /** 我自己在把關者名單上的那一筆（不在名單上就是 undefined） */
  const myAssignee = useMemo(
    () => assignees.find((a) => String(_.get(a, "reviewer_id")) === String(me)),
    [assignees, me]
  );

  /*
   * 能不能按「通過／退回」。
   *
   * 正常情況看把關者名單：自己在名單上、而且還沒表態的人才有得按。
   * 名單取不到時（審核 DTO 的欄位名還在變動中）退而求其次用權責分立判斷 ——
   * 送審的人不能審自己送的東西，其餘的人先讓他按，真正的權限由後端擋。
   * 寧可讓不該按的人吃一個錯誤訊息，也不要讓真正的把關者找不到按鈕。
   */
  const canDecide = useMemo(() => {
    if (!pending) return false;
    if (assignees.length) return !!myAssignee && Number(_.get(myAssignee, "decision") || 0) === 0;
    return String(_.get(pending, "opened_by")) !== String(me);
  }, [pending, assignees, myAssignee, me]);

  const title = _.get(task, "title") || "";
  const projName = _.get(task, "project_name") || "";
  const desc = _.get(task, "description") || "";
  /** 「他做了什麼」＝回報的做法。後端兩個欄位名都出現過，兩個都吃 */
  const solution = _.get(task, "solution") || _.get(task, "resolution_note") || "";

  /** 型別徽章：審核型別（設計稿 L1242 的「程式檢查」）；沒有審核紀錄時退回工項型別 */
  const typeLabel = latest
    ? reviewTypeLabel(_.get(latest, "review_type"))
    : _.get(
        TYPE[typeKey(_.get(task, "issue_type_key") || _.get(task, "issue_type") || _.get(task, "type"))],
        "label",
        ""
      );

  /** 頁首那顆「等你看 N 天了」。沒有送審時間就不掛，不要憑空生一個天數出來 */
  const waitDays = pending ? daysSince(_.get(pending, "opened_at")) : null;

  /** 工項代碼：口頭與信件上大家用它指這一件事，不能只剩標題 */
  const code = _.get(task, "issue_key") || "";

  /** 「林怡君 9 月 17 日送出 · 新 WEBPOS」——每一段都缺得起，缺了就不寫那一段 */
  const subline = useMemo(() => {
    const parts = [];
    const who = latest ? nameOf(_.get(latest, "opened_by")) : "";
    const when = latest ? fmtDay(_.get(latest, "opened_at")) : "";
    if (who && when) parts.push(`${who} ${when}送出`);
    else if (who) parts.push(`${who} 送出`);
    else if (when) parts.push(`${when}送出`);
    if (projName) parts.push(projName);
    if (code) parts.push(code);
    return parts.join(" · ");
  }, [latest, projName, code, nameOf]);

  /*
   * 他交出來的檔案：回報進度時夾的檔案 + 工項附件，最新的在最上面。
   *
   * 這一頁是審核者真正做決定的地方（「今天」與審核清單都導到這裡），交付物不能只躲在
   * 工項頁「更多欄位 → 審核」裡 —— 審核者看不到檔案，就只能不看就按通過。
   * 兩種來源都要列：負責人常用「我的回報」卡旁的「＋ 加入附件」放交付物，那是工項附件。
   */
  const deliverables = useMemo(() => {
    const fromReports = records
      .filter((r) => r && r.id != null && !!(_.get(r, "has_file") ?? _.get(r, "file_name")))
      .map((r) => ({
        key: `p-${r.id}`,
        url: `/MyTask/Progress/Download/${r.id}`,
        name: _.get(r, "file_name") || "attachment",
        source: "回報進度時附加",
        size: fmtSize(_.get(r, "file_size")),
        who: nameOf(_.get(r, "create_id")),
        at: _.get(r, "create_at"),
      }));
    const fromTask = attachments
      .filter((a) => a && a.id != null)
      .map((a) => ({
        key: `a-${a.id}`,
        url: `/Task/Attachment/Download/${a.id}`,
        name: _.get(a, "file_name") || "download",
        source: "工項附件",
        size: fmtSize(_.get(a, "file_size")),
        who: nameOf(_.get(a, "create_id")),
        at: _.get(a, "create_at"),
      }));
    return _.orderBy([...fromReports, ...fromTask], [(f) => timeOf(f.at)], ["desc"]);
  }, [records, attachments, nameOf]);

  /*
   * 他回報了什麼：進度回報的**內容**，最新的在最上面。
   *
   * 在此之前 records 只被拿來挑出「有夾檔案」的那幾筆組 deliverables —— 回報的正文
   * 與內容裡貼的截圖從頭到尾沒有顯示過。而很多工項的交付物就是回報裡那張截圖
   * （工項頁「以前的紀錄」以前也只寫得出「[圖片×1]」），等於審核者什麼都沒看到
   * 就要按通過或退回。這一頁是決策點，回報內容不該只活在工項頁的收合區裡。
   *
   * 這裡用整段 RichView，不是工項頁那種「摘要一行 + 縮圖」：那邊是一列一件事的
   * 時間軸，撐開就讀不下去；這一頁是決策頁，版面長沒關係，看漏了才嚴重。
   * RichView 內部會做白名單消毒（回報是一般 API 收進來的 HTML，不可信）。
   */
  const reports = useMemo(() => {
    const pctOf = (r) => {
      const v = _.get(r, "progress");
      const n = v == null || v === "" ? NaN : Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const desc = _.orderBy(
      (records || []).filter(Boolean),
      [(r) => timeOf(_.get(r, "create_at")), (r) => Number(_.get(r, "id")) || 0],
      ["desc", "desc"]
    );
    return desc.map((r, i) => {
      const html = String(_.get(r, "content") || "");
      const pv = pctOf(r);
      // 上一次是幾 %：desc 是由新到舊，往後找第一筆有值的那一筆。找不到就只寫現在的值
      let prev = null;
      for (let j = i + 1; j < desc.length; j += 1) {
        const q = pctOf(desc[j]);
        if (q != null) {
          prev = q;
          break;
        }
      }
      return {
        key: `r-${_.get(r, "id") ?? i}`,
        html,
        /*
         * 「這一筆到底有沒有東西」要連圖一起看：只貼了一張截圖、一個字都沒寫的回報
         * 純文字是空的，用純文字判斷會把它畫成一列空白，看起來像壞掉。
         */
        empty: !toPlainText(html) && !/<img/i.test(html),
        hasShot: /<img/i.test(html),
        who: nameOf(_.get(r, "create_id")) || _.get(r, "create_id") || "",
        when: fmtDay(_.get(r, "create_at")),
        pct: pv == null ? "" : prev != null && prev !== pv ? `進度 ${prev}% → ${pv}%` : `進度 ${pv}%`,
        /** 依 commit 事後補登的（不計入績效）—— 不是當時親手回報的，審核時要分得出來 */
        backfill: !!(_.get(r, "is_backfill") ?? _.get(r, "isBackfill")),
      };
    });
  }, [records, nameOf]);

  /** 有沒有圖決定要不要寫「點圖看原尺寸」那句話 —— 沒有圖就不要教人點不存在的東西 */
  const reportHasShot = reports.some((r) => r.hasShot);

  const download = useCallback(
    async (f) => {
      try {
        await downloadBlob(f.url, f.name);
      } catch (e) {
        toast(await downloadErrMsg(e), "error");
      }
    },
    [toast]
  );

  /* ---------- 其他資料（全部收進一個收合區） ---------- */

  const est = Number(_.get(task, "estimated_hours") || 0);
  const act = Number(_.get(task, "actual_hours") || 0);
  const updatedAt = _.get(task, "update_at") || _.get(task, "updated_at") || _.get(task, "updateAt");

  const facts = [];
  if (est) facts.push(`預估 ${est} 小時`);
  if (act) facts.push(`實際 ${act} 小時`);
  if (attachments.length) facts.push(`附件 ${attachments.length} 個`);
  if (updatedAt && fmtDay(updatedAt)) facts.push(`${fmtDay(updatedAt)}更新`);

  /** 多簽核人：一張審核單可以掛好幾個把關者，各自的決定要分開看得到 */
  const reviewerLine = assignees
    .map((a) => {
      const n = nameOf(_.get(a, "reviewer_id"));
      return n ? `${n}（${decisionLabel(_.get(a, "decision"))}）` : "";
    })
    .filter(Boolean)
    .join("、");

  const moreLabels = [];
  if (est || act) moreLabels.push("工時");
  if (attachments.length) moreLabels.push("附件");
  if (reviewerLine) moreLabels.push("審核人");
  if (reassigns.length) moreLabels.push("改派");
  if (reviews.length) moreLabels.push("審核紀錄");
  moreLabels.push("完整內容");

  /*
   * 按「通過」會怎樣 —— 按之前就要講，而且寫成提示條（硬規則 4）。
   *
   * 審核通過之後不必再有人去按一次「完成」：後端 ReviewService.Decide 在整張審核單
   * 通過時自己把工項推到「已完成」、有人要求修改時退回「進行中」。
   * 但「按了就完成」只在兩個條件都成立時才是真的，所以依現況挑一句，不一律寫同一句：
   *   · 同一張審核單還有別的把關者沒表態 → 我通過只是一票，要等他們；
   *   · 還有驗收條件沒勾 → 審核會過，但「審核中 → 已完成」的守門（all_ac_checked）
   *     可能把自動完成擋下來，工項會停在審核中。守門是工作流資料、各環境不一定一樣，
   *     所以只能寫「可能」，不能保證一定會擋、也不能保證一定不擋。
   */
  const othersPending = assignees
    .filter((a) => a !== myAssignee && Number(_.get(a, "decision") || 0) === 0)
    .map((a) => nameOf(_.get(a, "reviewer_id")))
    .filter(Boolean);
  const uncheckedCount = criteria.filter((c) => !(_.get(c, "is_checked") ?? _.get(c, "isChecked"))).length;
  let passNote;
  if (othersPending.length > 0) {
    passNote = {
      tone: "info",
      text: `您按「通過」後，仍須等待 ${othersPending.join("、")} 通過；全數通過後才會變為「已完成」。任一人退回，此工項即回到「進行中」。`,
    };
  } else if (uncheckedCount > 0) {
    passNote = {
      tone: "warn",
      text: `尚有 ${uncheckedCount} 條驗收條件未勾選。按「通過」後審核即通過，但此工項可能停留在「審核中」，不會自動變為「已完成」，須待負責人勾選所有條件。`,
    };
  } else {
    passNote = {
      tone: "info",
      text: "按「通過」後，此工項將直接變為「已完成」，無須再手動完成。按「退回修改」則回到「進行中」（進度超過 90% 者調回 90%），負責人修改完成並將進度更新為 100% 後將重新送審。",
    };
  }

  /* ---------- 送出決定 ---------- */

  /** decision：1 = 通過、2 = 退回給他改（沿用後端 ReqReviewDecision 的定義） */
  const decide = useCallback(
    async (decision) => {
      if (!pending || busy) return;
      /* 退回一定要寫原因：只說「不行」而不說為什麼，對方只能猜，這件事會來回好幾趟 */
      if (decision === 2 && !note.trim()) {
        setNoteError("退回時請填寫原因，以利對方修改。");
        return;
      }
      setNoteError("");
      setBusy(true);
      try {
        await apiPostJson("/Review/Decide", {
          reviewId: _.get(pending, "id"),
          decision,
          comment: note.trim() || null,
        });
        /*
         * 工項狀態是後端自己推的（通過 → 已完成、要求修改 → 進行中），/Review/Decide 只回 ok。
         * 所以送出後重讀一次審核單與工項，toast 講的是讀回來的結果，不是「我按了哪一顆」——
         * 還有別的把關者沒表態、或自動轉換被守門擋下時，工項不會跟著變。
         * 兩支都用 safe 包：決定已經送出去了，重讀失敗不能讓人以為沒送成功。
         */
        const env = { Data: jwtEncode({ TaskId: Number(taskId) }), id: taskId, taskId };
        const [t2, rv2] = await Promise.all([
          safe(() => apiGet("/Task", env), null),
          safe(() => apiGet("/Review/List", { taskId }), null),
        ]);
        const mine = (_.get(rv2, "reviews", []) || []).find(
          (r) => String(_.get(r, "id")) === String(_.get(pending, "id"))
        );
        const out = reviewOutcome(
          decision,
          mine ? reviewStatusKey(_.get(mine, "status")) : null,
          t2 && _.get(t2, "status") != null ? statusKey(_.get(t2, "status")) : null,
          t2 ? _.get(t2, "progress", null) : null
        );
        toast(out.text, out.tone, out.ms);
        /* 回清單：那邊每次進來都重抓，剛決定的這一筆不會還掛在「等我審的」 */
        navigate("/Approvals");
      } catch (e) {
        toast(apiErrorMessage(e, "送出決定失敗"), "error");
      } finally {
        setBusy(false);
      }
    },
    [pending, busy, note, toast, navigate, taskId]
  );

  /* ---------- 畫面 ---------- */

  /* 回清單的入口一直都在：審完不一定要按通過／退回，也可能只是看看 */
  const back = (
    <Button variant="text" type="button" startIcon={<MdChevronLeft aria-hidden="true" />} onClick={() => navigate("/Approvals")}>
      返回審核清單
    </Button>
  );

  /* 三個 RichView 共用同一組內文樣式（RichView 收的是 CSS 字串） */
  const richStyle = "font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;";

  if (loading) {
    return (
      <>
        {back}
        <PageLoading sx={{ mt: 3 }} />
      </>
    );
  }

  if (!task) {
    return (
      <>
        {back}
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2" component="p">
            無法開啟此工項
          </Typography>
          <Typography sx={{ mt: 1 }}>此工項可能已被刪除，或您沒有檢視權限。請返回清單後再試一次。</Typography>
        </Box>
      </>
    );
  }

  const waitTone = waitDays !== null ? toneByWait(waitDays) : null;

  return (
    <>
      {back}

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 1.5 }}>
        {typeLabel && <StatusChip kind="type">{typeLabel}</StatusChip>}
        {waitDays !== null && (
          <StatusChip kind={waitTone} mark={waitTone === "attention" ? <MdWarningAmber /> : null}>
            {waitDays === 0 ? "今日送審" : `已等待 ${waitDays} 天`}
          </StatusChip>
        )}
      </Stack>

      <Typography variant="h1" component="h1" sx={{ mt: 1.5 }}>
        {title || "（未命名工項）"}
      </Typography>
      {subline && <Typography sx={{ mt: 1 }}>{subline}</Typography>}

      {/* 審核者真正要比對的兩件事並排在最上面（設計稿 L1252-L1268） */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 3 }}>
        <Card component="section">
          <CardContent>
            <Typography variant="h3" component="h2">
              需求內容
            </Typography>
            {desc ? (
              <Box sx={{ mt: 1 }}>
                {/* 需求裡常常貼截圖，RichView 會夾住圖寬、點圖還能放大看清楚 */}
                <RichView html={desc} sxStyle={richStyle} />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h2" component="p">
                  未填寫需求
                </Typography>
                <Typography sx={{ mt: 1 }}>未填寫需求即無從比對，可退回並請對方補上原先議定的內容。</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card component="section">
          <CardContent>
            <Typography variant="h3" component="h2">
              解決方式
            </Typography>
            {solution ? (
              <Box sx={{ mt: 1 }}>
                <RichView html={solution} sxStyle={richStyle} />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h2" component="p">
                  未填寫解決方式
                </Typography>
                <Typography sx={{ mt: 1 }}>未填寫解決方式即無法得知修改內容。退回時請對方說明修改範圍與測試方式。</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/*
        回報讀不到要講出來，而且要在決定之前講。
        這一頁上「他回報了什麼」與「他交出來的檔案」都是從 /MyTask/Progress 來的，
        讀不到的時候兩塊會一起消失（硬規則 2 的「空的不畫」在這裡會騙人）——
        把關者看到的是一張乾淨的畫面，會讀成「他沒回報過、沒交付物」然後按下退回。
        「讀不到」跟「沒有」是兩件事，不能長得一樣。
      */}
      {recordsFailed && (
        <Card component="section" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h3" component="h2">
              進度回報
            </Typography>
            <Typography sx={{ mt: 1 }}>
              無法讀取此工項的進度回報（含回報時附加的檔案），可能是您沒有檢視權限。
              這不代表負責人未曾回報或沒有交付物；下方檔案清單將不含回報時附加的檔案。
              決定前請先向負責人或專案經理確認。
            </Typography>
          </CardContent>
        </Card>
      )}

      {/*
        他回報了什麼：放在檔案清單之前 —— 先讀他寫了什麼、看他貼的截圖，再看檔案，最後才決定。
        沒有回報就整塊不畫（硬規則 2）；「沒有人回報過」這件事在下面的紀錄區已經講得出來。
      */}
      {reports.length > 0 && (
        <Card component="section" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h3" component="h2">
              進度回報（{reports.length} 筆）
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              依時間由新至舊排列。
              {reportHasShot ? "點選回報中的截圖可放大至原尺寸。" : ""}
            </Typography>
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              {reports.map((r) => (
                <Stack spacing={1} key={r.key}>
                  <Typography variant="body2" color="text.secondary">
                    {[r.who, r.when, r.pct].filter(Boolean).join("　")}
                    {r.backfill && (
                      <StatusChip kind="closed" size="small" mark={<MdRemove />} component="span" sx={{ ml: 1 }}>
                        事後補登
                      </StatusChip>
                    )}
                  </Typography>
                  {r.empty ? (
                    /* 只按了進度、沒有寫內容的回報：寫出來比留一列空白好，空白會被讀成「壞掉了」 */
                    <Typography>（僅更新進度，未填寫內容）</Typography>
                  ) : (
                    <RichView html={r.html} sxStyle={richStyle} />
                  )}
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 他交出來的檔案：放在「你的決定」前面 —— 先看交付物，再決定 */}
      {deliverables.length > 0 && (
        <Card component="section" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h3" component="h2">
              交付檔案（{deliverables.length} 個）
            </Typography>
            {(canDecide || deliverables.length > 1) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {canDecide ? "按「通過」或「退回修改」前，請先下載檢視。" : ""}
                {deliverables.length > 1 ? "依時間由新至舊排列。" : ""}
              </Typography>
            )}
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {deliverables.map((f) => (
                <Stack
                  direction="row"
                  spacing={1.5}
                  useFlexGap
                  sx={{ flexWrap: "wrap", alignItems: "center" }}
                  key={f.key}
                >
                  <Typography component="span" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    {f.name}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" · "}
                      {[f.source, f.size, f.who, fmtDay(f.at)].filter(Boolean).join(" · ")}
                    </Typography>
                  </Typography>
                  <Button variant="text" type="button" onClick={() => download(f)}>
                    下載
                  </Button>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 你的決定（設計稿 L1270-L1284）。整頁只有這裡有一顆主要按鈕 */}
      {canDecide ? (
        <Card component="section" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h3" component="h2">
              審核決定
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <div>
                <Typography component="label" htmlFor="av-note" variant="subtitle1" sx={{ display: "block" }}>
                  審核意見
                </Typography>
                <TextField
                  placeholder="例如：需修改之處、修改後的注意事項"
                  id="av-note"
                  multiline
                  minRows={3}
                  fullWidth
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    if (noteError) setNoteError("");
                  }}
                  error={!!noteError}
                  helperText={
                    noteError ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <MdWarningAmber aria-hidden="true" />
                        {noteError}
                      </Box>
                    ) : (
                      "退回時請填寫原因，以利對方修改。"
                    )
                  }
                  sx={{ mt: 1 }}
                />
              </div>
              {/* 放在兩顆按鈕正上方：後果要在按之前讀到（理由見 passNote） */}
              <Banner kind={passNote.tone} icon={passNote.tone === "warn" ? <MdErrorOutline /> : <MdInfoOutline />}>
                {passNote.text}
              </Banner>
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="contained" type="button" disabled={busy} loading={busy} loadingPosition="start" onClick={() => decide(1)}>
                  通過
                </Button>
                <Button variant="outlined" type="button" disabled={busy} onClick={() => decide(2)}>
                  退回修改
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        /*
         * 不能決定的人要講清楚為什麼，而且講在版面正中間、不是灰字小字：
         * 使用者最常問的就是「為什麼我這裡沒有那兩顆按鈕」。
         */
        <Banner kind="info" icon={<MdInfoOutline />} sx={{ mt: 3 }}>
          {!latest && "此工項尚未送審，無須審核。"}
          {latest && !pending && (
            <>
              此審核已
              <strong>{REVIEW_STATUS_LABEL[latestKey] || "有結果"}</strong>
              ，無須再次審核。
            </>
          )}
          {/* 同一張審核單掛好幾個把關者時，自己表態過還是看得到這一頁，要講清楚在等誰 */}
          {pending && myAssignee && `您已完成審核（${decisionLabel(_.get(myAssignee, "decision"))}），正在等待其他審核人。`}
          {pending &&
            !myAssignee &&
            (String(_.get(pending, "opened_by")) === String(me)
              ? "此審核由您送出，須由審核人審核；依權責分立原則，送審者不可審核自己送出的項目。"
              : "您不在此審核的審核人名單中，無須審核。")}
        </Banner>
      )}

      {/*
        其餘資料一律收進這一塊（設計稿 L1279-L1284）。
        舊抽屜把 21 個區塊全部攤開，審核者得自己在裡面找重點 —— 收起來不是拿掉，
        點一下就看得到，但它不該跟「要求什麼／做了什麼」搶同一個位置。
        裡面每一小節都是「有資料才畫」，全部都沒有時整塊不畫（空的區塊不要畫出來）。
      */}
      {moreLabels.length > 0 && (
        <Disclosure title="其他資料" count={`（${moreLabels.join("、")}）`} sx={{ mt: 3 }}>
          <Stack spacing={2}>
            {facts.length > 0 && <Typography>{facts.join(" · ")}</Typography>}

            {reviewerLine && (
              <Stack spacing={1}>
                <Typography variant="h3" component="h3">
                  審核人
                </Typography>
                <Typography>{reviewerLine}</Typography>
              </Stack>
            )}

            {reassigns.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="h3" component="h3">
                  改派紀錄
                </Typography>
                {reassigns.map((x) => {
                  const from = String(_.get(x, "from") || "")
                    .split(",")
                    .filter(Boolean)
                    .map((acc) => nameOf(acc))
                    .join("、");
                  const to = nameOf(_.get(x, "to"));
                  const by = nameOf(_.get(x, "by"));
                  const at = fmtDay(_.get(x, "at"));
                  return (
                    <Typography key={`ra-${_.get(x, "id") || `${x.at}-${x.to}`}`}>
                      {at && `${at}　`}
                      {by && `${by} `}
                      {from ? `將審核人由 ${from} 改派為 ${to}` : `指定 ${to} 為審核人`}
                    </Typography>
                  );
                })}
              </Stack>
            )}

            {/*
              舊抽屜是把整支工項詳情（DoD、相依、留言、附件明細、程式碼、測試…）
              塞進審核裡。那些東西沒有消失，它們本來就住在工項頁上 ——
              這裡留一個入口過去，審核頁自己只負責「要求什麼／做了什麼」。
            */}
            <Stack spacing={1}>
              <Typography variant="h3" component="h3">
                完整內容
              </Typography>
              <Typography>驗收條件、相依關係、留言、附件明細、程式碼與測試紀錄請至工項頁檢視。</Typography>
              <div>
                <Button
                  variant="text"
                  type="button"
                  endIcon={<MdChevronRight aria-hidden="true" />}
                  onClick={() => navigate(`/Task/${taskId}`)}
                >
                  開啟工項頁
                </Button>
              </div>
            </Stack>

            {reviews.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="h3" component="h3">
                  審核紀錄
                </Typography>
                {reviews.map((r) => {
                  const k = reviewStatusKey(_.get(r, "status"));
                  const when = fmtDay(_.get(r, "opened_at"));
                  return (
                    <Typography key={`rv-${_.get(r, "id")}`}>
                      {when && `${when}　`}
                      {reviewTypeLabel(_.get(r, "review_type"))}：
                      {REVIEW_STATUS_LABEL[k] || "待審"}
                      {_.get(r, "comment") ? `　「${_.get(r, "comment")}」` : ""}
                    </Typography>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Disclosure>
      )}
    </>
  );
}

export default ApprovalView;
